import { addDays, daysBetween, startOfWeek, toISO, today } from '../lib/date'
import type { AppState, Application, CalEvent, ISODate, Subject, Task } from './types'

/** Events on a given day, ordered by start time (all-day items first). */
export function eventsOn(state: AppState, date: ISODate): CalEvent[] {
  return state.events
    .filter((e) => e.date === date)
    .sort((a, b) => (a.start ?? '').localeCompare(b.start ?? ''))
}

/** Everything scheduled from `date` forward, soonest first. */
export function upcomingEvents(state: AppState, from: ISODate = today(), limit = 20): CalEvent[] {
  return state.events
    .filter((e) => e.date >= from && !e.done)
    .sort((a, b) => a.date.localeCompare(b.date) || (a.start ?? '').localeCompare(b.start ?? ''))
    .slice(0, limit)
}

/** The single item to put in front of the user on the Today screen. */
export function nextUp(state: AppState): CalEvent | undefined {
  return upcomingEvents(state, today(), 1)[0]
}

/** Open tasks, most urgent first: overdue → due soonest → flagged → newest. */
export function openTasks(state: AppState): Task[] {
  const t = today()
  return state.tasks
    .filter((x) => !x.done)
    .sort((a, b) => {
      const ao = a.due && a.due < t ? 0 : 1
      const bo = b.due && b.due < t ? 0 : 1
      if (ao !== bo) return ao - bo
      if (a.due && b.due && a.due !== b.due) return a.due.localeCompare(b.due)
      if (!!a.due !== !!b.due) return a.due ? -1 : 1
      if (a.priority !== b.priority) return b.priority - a.priority
      return b.createdAt - a.createdAt
    })
}

export function tasksDueToday(state: AppState): Task[] {
  const t = today()
  return state.tasks.filter((x) => !x.done && x.due && x.due <= t)
}

/** Minutes studied per subject since the start of this week. */
export function weekStudy(state: AppState): {
  totalMins: number
  targetMins: number
  bySubject: { subject: Subject; mins: number }[]
  byDay: { date: ISODate; mins: number }[]
} {
  const weekStart = startOfWeek(today())
  const startMs = new Date(`${weekStart}T00:00:00`).getTime()
  const inWeek = state.sessions.filter((s) => s.at >= startMs)

  const bySubject = state.subjects.map((subject) => ({
    subject,
    mins: inWeek.filter((s) => s.subjectId === subject.id).reduce((n, s) => n + s.mins, 0),
  }))

  const byDay = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i)
    const mins = inWeek
      .filter((s) => toISO(new Date(s.at)) === date)
      .reduce((n, s) => n + s.mins, 0)
    return { date, mins }
  })

  return {
    totalMins: inWeek.reduce((n, s) => n + s.mins, 0),
    targetMins: state.subjects.reduce((n, s) => n + s.targetMins, 0),
    bySubject,
    byDay,
  }
}

/** Consecutive days ending today (or yesterday) with at least one session. */
export function studyStreak(state: AppState): number {
  const days = new Set(state.sessions.map((s) => toISO(new Date(s.at))))
  let streak = 0
  let cursor = today()
  // A streak survives "not yet studied today" — it only breaks on a missed day.
  if (!days.has(cursor)) cursor = addDays(cursor, -1)
  while (days.has(cursor)) {
    streak++
    cursor = addDays(cursor, -1)
  }
  return streak
}

export function appProgress(a: Application): number {
  if (!a.requirements.length) return 0
  return (a.requirements.filter((r) => r.done).length / a.requirements.length) * 100
}

/** The application with the nearest deadline that hasn't been submitted. */
export function primaryApplication(state: AppState): Application | undefined {
  const live = state.applications.filter(
    (a) => a.status !== 'declined' && a.status !== 'offer' && a.status !== 'submitted',
  )
  const pool = live.length ? live : state.applications
  return [...pool].sort((a, b) => a.deadline.localeCompare(b.deadline))[0]
}

/** All requirements across applications, flattened and sorted by urgency. */
export function upcomingRequirements(state: AppState, limit = 6) {
  const t = today()
  return state.applications
    .flatMap((a) =>
      a.requirements
        .filter((r) => !r.done)
        .map((r) => ({ app: a, req: r, due: r.due ?? a.deadline })),
    )
    .sort((x, y) => x.due.localeCompare(y.due))
    .filter((x) => daysBetween(t, x.due) > -365)
    .slice(0, limit)
}

export function auditionTracks(state: AppState) {
  return state.tracks.filter((t) => t.audition)
}
